import DOMPurify, { type Config } from 'dompurify'
import { useCallback, useEffect, useRef, useState } from 'react'

interface ToolHtmlReportState {
  html: string | null
  isLoading: boolean
  isError: boolean
}

const htmlCache = new Map<string, string>()

const DOMPURIFY_CONFIG: Config = {
  ALLOWED_TAGS: [
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'p',
    'div',
    'span',
    'section',
    'table',
    'thead',
    'tbody',
    'tr',
    'th',
    'td',
    'ul',
    'ol',
    'li',
    'dl',
    'dt',
    'dd',
    'strong',
    'em',
    'code',
    'pre',
    'a',
    'br',
    'hr',
    'img',
  ],
  ALLOWED_ATTR: ['style', 'class', 'href', 'src', 'alt', 'colspan', 'rowspan', 'lang'],
}

/**
 * Fetches HTML content from S3 via presigned URL, sanitizes it with DOMPurify,
 * and caches the result. Used for rendering tool call HTML reports in split view.
 */
export const useToolHtmlReport = (
  htmlResultKey: string | undefined,
  fetchPresignedUrl: (key: string) => Promise<string>
): ToolHtmlReportState => {
  const [state, setState] = useState<ToolHtmlReportState>({
    html: null,
    isLoading: false,
    isError: false,
  })
  const abortRef = useRef<AbortController | null>(null)

  const fetchHtml = useCallback(
    async (key: string) => {
      const cached = htmlCache.get(key)
      if (cached) {
        setState({ html: cached, isLoading: false, isError: false })
        return
      }

      setState({ html: null, isLoading: true, isError: false })
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller

      try {
        const presignedUrl = await fetchPresignedUrl(key)
        const response = await fetch(presignedUrl, { signal: controller.signal })

        if (!response.ok) {
          setState({ html: null, isLoading: false, isError: true })
          return
        }

        const rawHtml = await response.text()
        const sanitized = DOMPurify.sanitize(rawHtml, DOMPURIFY_CONFIG)
        htmlCache.set(key, sanitized)
        setState({ html: sanitized, isLoading: false, isError: false })
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return
        setState({ html: null, isLoading: false, isError: true })
      }
    },
    [fetchPresignedUrl]
  )

  useEffect(() => {
    if (!htmlResultKey) return

    fetchHtml(htmlResultKey)

    return () => {
      abortRef.current?.abort()
    }
  }, [htmlResultKey, fetchHtml])

  return state
}
